import { useCallback, useRef } from 'react';

export type TrimSnapshot = {
    offsetFrame: number;
    durationFrame: number | null;
};

const DEBOUNCE_MS = 400;

const useTrimHistory = (initialSnapshot: TrimSnapshot) => {
    const historyRef = useRef<TrimSnapshot[]>([initialSnapshot]);
    const cursorRef = useRef(0);
    const lastCommitTimeRef = useRef(0);
    const isRestoringRef = useRef(false);

    const recordChange = useCallback((offsetFrame: number, durationFrame: number | null, forceNewEntry = false) => {
        if (isRestoringRef.current) {
            return;
        }

        const now = Date.now();
        const timeSinceLastCommit = now - lastCommitTimeRef.current;
        const newSnapshot: TrimSnapshot = { offsetFrame, durationFrame };

        if (!forceNewEntry && timeSinceLastCommit < DEBOUNCE_MS && cursorRef.current > 0) {
            historyRef.current[cursorRef.current] = newSnapshot;
        } else {
            historyRef.current = historyRef.current.slice(0, cursorRef.current + 1);
            historyRef.current.push(newSnapshot);
            cursorRef.current++;
        }

        lastCommitTimeRef.current = now;
    }, []);

    const undo = useCallback((): TrimSnapshot | null => {
        if (cursorRef.current <= 0) {
            return null;
        }

        cursorRef.current--;
        isRestoringRef.current = true;
        const snapshot = historyRef.current[cursorRef.current];

        requestAnimationFrame(() => {
            isRestoringRef.current = false;
        });

        return snapshot;
    }, []);

    const redo = useCallback((): TrimSnapshot | null => {
        if (cursorRef.current >= historyRef.current.length - 1) {
            return null;
        }

        cursorRef.current++;
        isRestoringRef.current = true;
        const snapshot = historyRef.current[cursorRef.current];

        requestAnimationFrame(() => {
            isRestoringRef.current = false;
        });

        return snapshot;
    }, []);

    return { recordChange, undo, redo };
};

export default useTrimHistory;
